import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image/Image'
import GeoContext from 'contexts/geoContext'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import { getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { convertAudToRegion } from 'luxPlus/lib/getRegionSaveAmountCopy'
import { useAppSelector } from 'hooks/reduxHooks'

interface Notification {
  id: number
  destination: string
  nights: number
  offerName: string
  savedAmountAud: number
  imageId: string
}

const NOTIFICATIONS: Array<Notification> = [
  {
    id: 1,
    destination: 'Fiji',
    nights: 7,
    offerName: 'Award-Winning Castaway Island Paradise',
    savedAmountAud: 5590,
    imageId: 'h1t0c6c9brq83bwhazx',
  },
  {
    id: 2,
    destination: 'Maldives',
    nights: 7,
    offerName: 'Five-Star Maldives Escape on Secluded Turtle Island',
    savedAmountAud: 2205,
    imageId: 'dapbr82l65bcj5iy6v8',
  },
  {
    id: 3,
    destination: 'Egypt',
    nights: 16,
    offerName: 'Premium 17-Day Egypt & Jordan Opulence 2026',
    savedAmountAud: 2080,
    imageId: 'm4rcv17j80xrikxq419',
  },
  {
    id: 4,
    destination: 'Indonesia',
    nights: 7,
    offerName: 'Private Balinese Villa Haven in Sanur',
    savedAmountAud: 2057,
    imageId: 'vg4uvt2y5dgkivujuba6',
  },
  {
    id: 5,
    destination: 'Czech Republic',
    nights: 24,
    offerName: 'Ultimate Europe by Private Charter Flight: 25-Day Tour',
    savedAmountAud: 2000,
    imageId: 'wbk1yjnf56awezs1lu',
  },
  {
    id: 6,
    destination: 'Vietnam',
    nights: 13,
    offerName: 'Deluxe 14-Day Vietnam & Cambodia Discovery 2026',
    savedAmountAud: 1799,
    imageId: '7pmj8f3m1cz7ip3920k',
  },
  {
    id: 7,
    destination: 'Japan',
    nights: 13,
    offerName: 'Deluxe 14-Day Best of Japan',
    savedAmountAud: 1700,
    imageId: '6tkmq2mo1izznyjyfln',
  },
  {
    id: 8,
    destination: 'Thailand',
    nights: 10,
    offerName: 'Award-Winning Koh Samui Clifftop Pool Villas',
    savedAmountAud: 1353,
    imageId: 'lids8h5rs9zj2xvkxr',
  },
  {
    id: 9,
    destination: 'United Kingdom',
    nights: 20,
    offerName: 'London Heritage Glamour: Victoria Embankment Escape',
    savedAmountAud: 1160,
    imageId: 'bh52x5crz46nt54uxsl',
  },
  {
    id: 10,
    destination: 'South Africa',
    nights: 8,
    offerName: 'Deluxe 9-Day Best of South Africa Zimbabwe & Botswana',
    savedAmountAud: 1200,
    imageId: 'v2u8uob7mgdu7o6y6njq',
  },
]

const VISIBLE_COUNT_DESKTOP = 3
const VISIBLE_COUNT_MOBILE = 2
const CYCLE_INTERVAL_MS = 5000
const CARD_GAP = 9
const TRANSITION_DURATION_MS = 800
const SHADOW_PADDING = 24
const CARD_INNER_PADDING = 16
const MOBILE_BACK_SCALE = 0.85
const MOBILE_BACK_OPACITY = 0.5
const MOBILE_PEEK = CARD_INNER_PADDING * MOBILE_BACK_SCALE

const SectionBackground = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(24)} 0;
  margin-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)} 0;
  }
`

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    gap: ${rem(56)};
    align-items: center;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  justify-content: center;

  ${mediaQueryUp.desktop} {
    flex: 0 0 auto;
    width: ${rem(494)};
    padding: ${rem(64)} 0;
  }
`

const DesktopStackBreakpoint = styled(CSSBreakpoint)`
  flex: 1 0 0;
  min-width: 0;
`

const NotificationStack = styled.div`
  position: relative;
  overflow: hidden;
  padding: ${rem(SHADOW_PADDING)};
  margin: ${rem(-SHADOW_PADDING)};
`

const CardStackItem = styled.div<{ $top: number; $opacity: number; $scale: number }>`
  position: absolute;
  left: ${rem(SHADOW_PADDING)};
  right: ${rem(SHADOW_PADDING)};
  top: ${props => rem(props.$top)};
  opacity: ${props => props.$opacity};
  transform: scale(${props => props.$scale});
  transform-origin: top center;
  transition: top ${TRANSITION_DURATION_MS}ms ease, opacity ${TRANSITION_DURATION_MS}ms ease, transform ${TRANSITION_DURATION_MS}ms ease;
`

const BaseCardRoot = styled.div`
  display: flex;
  gap: ${rem(16)};
  align-items: center;
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${props => props.theme.borderRadius.M};
  box-shadow:
    0 ${rem(1)} ${rem(4)} 0 rgba(0, 0, 0, 0.08),
    0 ${rem(4)} ${rem(16)} ${rem(2)} rgba(0, 0, 0, 0.12);
`

const NotificationCardRoot = styled(BaseCardRoot)`
  min-height: ${rem(135)};
`

const MobileNotificationStack = styled.div`
  position: relative;
  overflow: hidden;
  padding: ${rem(SHADOW_PADDING)};
  margin: ${rem(-SHADOW_PADDING)};
  max-width: ${rem(590)};
`

const MobileCardStackItem = styled.div<{ $top: number; $opacity: number; $scale: number; $zIndex: number }>`
  position: absolute;
  left: ${rem(SHADOW_PADDING)};
  right: ${rem(SHADOW_PADDING)};
  top: ${props => rem(props.$top)};
  opacity: ${props => props.$opacity};
  transform: scale(${props => props.$scale});
  transform-origin: top center;
  z-index: ${props => props.$zIndex};
  transition: top ${TRANSITION_DURATION_MS}ms ease, opacity ${TRANSITION_DURATION_MS}ms ease, transform ${TRANSITION_DURATION_MS}ms ease;
`

const NotificationImageWrapper = styled.div`
  width: ${rem(80)};
  height: ${rem(80)};
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    width: ${rem(103)};
    height: ${rem(103)};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const StatValue = styled.span`
  color: ${props => props.theme.palette.highlight.secondary.normalForeground};
  font-weight: 600;
`

interface VisibleCard {
  notification: Notification
  slot: number
  top: number
  opacity: number
  scale: number
}

function getSlotStyle(slot: number): { opacity: number; scale: number } {
  if (slot <= -1) return { opacity: 0, scale: 1 }
  if (slot === 0) return { opacity: 1, scale: 1 }
  if (slot === 1) return { opacity: 0.7, scale: 0.95 }
  if (slot === 2) return { opacity: 0.4, scale: 0.9 }
  return { opacity: 0, scale: 0.9 }
}

function getMobileSlotStyle(slot: number): { opacity: number; scale: number } {
  if (slot <= -1) return { opacity: 0, scale: 1 }
  if (slot === 0) return { opacity: 1, scale: 1 }
  if (slot === 1) return { opacity: MOBILE_BACK_OPACITY, scale: MOBILE_BACK_SCALE }
  return { opacity: 0, scale: MOBILE_BACK_SCALE }
}

interface NotificationCardProps {
  notification: Notification
  regionCode: string
}

function NotificationCardContent({ notification, regionCode }: NotificationCardProps) {
  const localSaving = convertAudToRegion(notification.savedAmountAud, regionCode)

  return <>
    <NotificationImageWrapper>
      <Image id={notification.imageId} fit="center" dpr={2} alt="" />
    </NotificationImageWrapper>
    <Group direction="vertical" gap={4}>
      <BodyText variant="large">
        <FormattedMessage
          id="6OD0dvKmDCJAQ0A8yWRw"
          defaultMessage="<strong>A member</strong> booked {nights} nights in {destination}"
          description="Notification card text showing a member booked a trip with destination and duration"
          values={{
            nights: notification.nights,
            destination: notification.destination,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </BodyText>
      <BodyText variant="large" colour="highlight-secondary" weight="bold">
        <FormattedMessage
          id="KQSi7VR3iL757xbSZbo1"
          defaultMessage="Saved {amount} with {programName}"
          description="Notification card text showing amount saved with LuxPlus membership"
          values={{
            amount: <FormatCurrency value={localSaving} format="rounded" />,
            programName: LUXURY_PLUS.PROGRAM_NAME,
          }}
        />
      </BodyText>
    </Group>
  </>
}

function LuxPlusLandingMembershipSection() {
  const intl = useIntl()
  const [baseIndex, setBaseIndex] = useState(0)
  const [phase, setPhase] = useState<'idle' | 'entering' | 'shifting'>('idle')
  const cardRef = useRef<HTMLDivElement>(null)
  const [cardHeight, setCardHeight] = useState(135)
  const mobileCardRef = useRef<HTMLDivElement>(null)
  const [mobileCardHeight, setMobileCardHeight] = useState(100)
  const { currentRegionCode } = useContext(GeoContext)
  const { handleBecomeMember, handleStartFreePreview } = useLuxPlusLandingActions()
  const isPreview = useAppSelector(getIsFreePreviewLuxPlusMember)

  const oneMembershipText = intl.formatMessage({
    id: 'QbsSQvTygmeH_D2lU8FZ',
    defaultMessage: 'One membership.',
    description: 'Membership section heading first line',
  })

  const enjoyDiscountsText = intl.formatMessage({
    id: 'rZubHR-WIdTu7MM63Xhv',
    defaultMessage: 'Enjoy discounts across hotels, tours & experiences worldwide',
    description: 'Membership section subtitle about available discounts across travel categories',
  })

  const savedByMembersText = intl.formatMessage({
    id: 'dGQOg8F0DZbhHC7Yu6gr',
    defaultMessage: 'saved by members in the past 30 days',
    description: 'Label for total amount saved by members in the past 30 days stat',
  })

  const offersWithDiscountsText = intl.formatMessage({
    id: 'z2k-NHd1U3sR4__AkVYy',
    defaultMessage: 'ways members have saved',
    description: 'Label for number of ways members have saved stat',
  })

  const becomeMemberText = intl.formatMessage({
    id: '_AZBFSuwla78odDeqD18',
    defaultMessage: 'Become a member',
    description: 'CTA button text to become a LuxPlus member',
  })

  const startFreePreviewText = intl.formatMessage({
    id: 'ITOnaXEJo2SHHJVEX8Yb',
    defaultMessage: 'Start free preview',
    description: 'CTA button text to start a free LuxPlus preview',
  })

  const cancelAnytimeText = intl.formatMessage({
    id: 'u4LrLBJj283E87o74jhl',
    defaultMessage: 'Cancel anytime · No booking minimums',
    description: 'Disclaimer text about membership cancellation policy and no booking minimums',
  })

  useEffect(() => {
    // Prevent mid-animation changes
    const interval = setInterval(() => {
      setPhase((prev) => prev === 'idle' ? 'entering' : prev)
    }, CYCLE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    // Wait one painted frame at slot -1 so CSS transitions on new cards have a starting position
    if (phase !== 'entering') return
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPhase('shifting')
      })
    })
    return () => cancelAnimationFrame(raf)
  }, [phase])

  useEffect(() => {
    if (phase !== 'shifting') return
    const timeout = setTimeout(() => {
      setBaseIndex((prev) => (prev - 1 + NOTIFICATIONS.length) % NOTIFICATIONS.length)
      setPhase('idle')
    }, TRANSITION_DURATION_MS)
    return () => clearTimeout(timeout)
  }, [phase])

  useEffect(() => {
    const el = cardRef.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      setCardHeight(el.getBoundingClientRect().height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [baseIndex])

  useEffect(() => {
    const el = mobileCardRef.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      setMobileCardHeight(el.getBoundingClientRect().height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [baseIndex])

  const visibleCards = useMemo<Array<VisibleCard>>(() => {
    const len = NOTIFICATIONS.length

    if (phase === 'idle') {
      const cards: Array<VisibleCard> = []
      let top = SHADOW_PADDING
      for (let i = 0; i < VISIBLE_COUNT_DESKTOP; i++) {
        const notification = NOTIFICATIONS[(baseIndex + i) % len]
        const { opacity, scale } = getSlotStyle(i)
        cards.push({ notification, slot: i, top, opacity, scale })
        top += cardHeight * scale + CARD_GAP
      }
      return cards
    }

    const enteringIndex = (baseIndex - 1 + len) % len
    const indices = [
      enteringIndex,
      baseIndex,
      (baseIndex + 1) % len,
      (baseIndex + 2) % len,
    ]

    const enteringStyle = getSlotStyle(-1)
    let top = phase === 'entering'
      ? SHADOW_PADDING - cardHeight * enteringStyle.scale - CARD_GAP
      : SHADOW_PADDING

    return indices.map((notifIdx, i) => {
      const slot = phase === 'entering' ? i - 1 : i
      const notification = NOTIFICATIONS[notifIdx]
      const { opacity, scale } = getSlotStyle(slot)
      const card = { notification, slot, top, opacity, scale }
      top += cardHeight * scale + CARD_GAP
      return card
    })
  }, [baseIndex, phase, cardHeight])

  const mobileVisibleCards = useMemo<Array<VisibleCard>>(() => {
    const len = NOTIFICATIONS.length
    const backTop = SHADOW_PADDING + mobileCardHeight * (1 - MOBILE_BACK_SCALE) + MOBILE_PEEK

    function topForSlot(slot: number): number {
      if (slot <= -1) return SHADOW_PADDING - mobileCardHeight - CARD_GAP
      if (slot === 0) return SHADOW_PADDING
      if (slot === 1) return backTop
      return backTop + mobileCardHeight * MOBILE_BACK_SCALE
    }

    if (phase === 'idle') {
      return Array.from({ length: VISIBLE_COUNT_MOBILE }, (_, i) => {
        const notification = NOTIFICATIONS[(baseIndex + i) % len]
        const { opacity, scale } = getMobileSlotStyle(i)
        return { notification, slot: i, top: topForSlot(i), opacity, scale }
      })
    }

    const enteringIndex = (baseIndex - 1 + len) % len
    const indices = [enteringIndex, baseIndex, (baseIndex + 1) % len]

    return indices.map((notifIdx, i) => {
      const slot = phase === 'entering' ? i - 1 : i
      const notification = NOTIFICATIONS[notifIdx]
      const { opacity, scale } = getMobileSlotStyle(slot)
      return { notification, slot, top: topForSlot(slot), opacity, scale }
    })
  }, [baseIndex, phase, mobileCardHeight])

  const mobileStackStyle = useMemo(() => ({
    height: rem(mobileCardHeight + MOBILE_PEEK + 2 * SHADOW_PADDING),
  }), [mobileCardHeight])

  const stackStyle = useMemo(() => {
    // Calculate height of stack to match visible cards
    let h = SHADOW_PADDING
    for (let i = 0; i < VISIBLE_COUNT_DESKTOP; i++) {
      const { scale } = getSlotStyle(i)
      h += cardHeight * scale
      if (i < VISIBLE_COUNT_DESKTOP - 1) h += CARD_GAP
    }
    h += SHADOW_PADDING
    return { height: rem(h) }
  }, [cardHeight])

  return (
    <SectionBackground>
      <LayoutContainer>
        <SectionContent>
          <LeftColumn>
            <Group direction="vertical" gap={4}>
              <Heading variant="heading2">
                {oneMembershipText}<br />
                <FormattedMessage
                  id="uf7hiy2MUBQQfCjN6IqF"
                  defaultMessage="<em>Thousands</em> of ways to save."
                  description="Membership section heading second line emphasizing thousands of savings opportunities"
                  values={{
                    em: (chunks) => <em>{chunks}</em>,
                  }}
                />
              </Heading>
              <BodyText variant="medium">
                {enjoyDiscountsText}
              </BodyText>
            </Group>

            <Group direction="vertical" gap={8}>
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <StatValue><Heading variant="heading4" colour="highlight-secondary"><FormatCurrency value={convertAudToRegion(2828747, currentRegionCode)} format="rounded" /></Heading></StatValue>
                <Caption variant="medium">{savedByMembersText}</Caption>
              </Group>
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <StatValue><Heading variant="heading4" colour="highlight-secondary">108,000</Heading></StatValue>
                <Caption variant="medium">{offersWithDiscountsText}</Caption>
              </Group>
            </Group>

            <CSSBreakpoint max="tablet">
              <MobileNotificationStack style={mobileStackStyle}>
                {mobileVisibleCards.map(({ notification, slot, top, opacity, scale }) => (
                  <MobileCardStackItem
                    key={notification.id}
                    $top={top}
                    $opacity={opacity}
                    $scale={scale}
                    $zIndex={10 - slot}
                  >
                    <BaseCardRoot ref={slot === 0 ? mobileCardRef : undefined}>
                      <NotificationCardContent
                        notification={notification}
                        regionCode={currentRegionCode}
                      />
                    </BaseCardRoot>
                  </MobileCardStackItem>
                ))}
              </MobileNotificationStack>
            </CSSBreakpoint>

            <ButtonGroup>
              <TextButton kind="primary" fit="mobile-full-width" onClick={handleBecomeMember}>
                {becomeMemberText}
              </TextButton>
              {!isPreview && (
                <TextButton kind="secondary" fit="mobile-full-width" onClick={handleStartFreePreview}>
                  {startFreePreviewText}
                </TextButton>
              )}
            </ButtonGroup>

            <BodyText variant="small" colour="neutral-one">
              {cancelAnytimeText}
            </BodyText>
          </LeftColumn>

          <DesktopStackBreakpoint min="desktop">
            <NotificationStack style={stackStyle}>
              {visibleCards.map(({ notification, slot, top, opacity, scale }) => (
                <CardStackItem
                  key={notification.id}
                  $top={top}
                  $opacity={opacity}
                  $scale={scale}
                >
                  <NotificationCardRoot ref={slot === 0 ? cardRef : undefined}>
                    <NotificationCardContent
                      notification={notification}
                      regionCode={currentRegionCode}
                    />
                  </NotificationCardRoot>
                </CardStackItem>
              ))}
            </NotificationStack>
          </DesktopStackBreakpoint>
        </SectionContent>
      </LayoutContainer>
    </SectionBackground>
  )
}

export default LuxPlusLandingMembershipSection
